import { ReportData } from '../types/reportTypes';

/**
 * Represents a report stored in localStorage
 */
export interface StoredReport {
  id: string;
  title: string;
  reportType: 'standard' | 'cody';
  period: { month: number; year: number };
  generationMode?: 'monthly' | 'quarterly' | 'ytd'; // undefined = monthly (backward compat)
  data: ReportData;
  createdAt: string; // ISO date string for JSON serialization
}

/**
 * Service for persisting generated marketing reports to localStorage
 */
class ReportStorageService {
  private readonly STORAGE_KEY = 'roi-generated-reports';

  /**
   * Save a new report to localStorage
   */
  saveReport(
    title: string,
    reportType: 'standard' | 'cody',
    period: { month: number; year: number },
    data: ReportData,
    generationMode?: 'monthly' | 'quarterly' | 'ytd'
  ): string {
    const id = this.generateUUID();
    const report: StoredReport = {
      id,
      title,
      reportType,
      period,
      generationMode,
      data,
      createdAt: new Date().toISOString()
    };

    const reports = this.getReports();
    reports.push(report);
    this.saveToStorage(reports);

    return id;
  }

  /**
   * Get all saved reports, sorted by creation date (newest first)
   */
  getReports(): StoredReport[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];

      const reports = JSON.parse(data) as StoredReport[];
      return reports.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error loading reports from localStorage:', error);
      return [];
    }
  }

  /**
   * Get a single report by ID
   */
  getReport(id: string): StoredReport | null {
    const reports = this.getReports();
    return reports.find(r => r.id === id) || null;
  }

  /**
   * Delete a report by ID
   */
  deleteReport(id: string): boolean {
    const reports = this.getReports();
    const filteredReports = reports.filter(r => r.id !== id);

    if (filteredReports.length === reports.length) {
      return false; // Report not found
    }

    this.saveToStorage(filteredReports);
    return true;
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): { count: number; sizeKB: number } {
    const reports = this.getReports();
    const data = localStorage.getItem(this.STORAGE_KEY) || '[]';
    const sizeBytes = new Blob([data]).size;

    return {
      count: reports.length,
      sizeKB: Math.round(sizeBytes / 1024 * 100) / 100
    };
  }

  /**
   * Save reports array to localStorage
   */
  private saveToStorage(reports: StoredReport[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(reports));
    } catch (error) {
      console.error('Error saving reports to localStorage:', error);
      throw new Error('Failed to save report to storage');
    }
  }

  /**
   * Generate a UUID for report IDs
   * Falls back to timestamp + random if crypto.randomUUID is unavailable
   */
  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const reportStorageService = new ReportStorageService();
